import React from 'react';
import { Box, Toolbar, Alert, Button } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { setCredentials } from '../../store/slices/authSlice';
import Header from './Header';
import Sidebar from './Sidebar';
import NotificationSnackbar from '../common/NotificationSnackbar';

const DRAWER_WIDTH = 280;
const MINI_DRAWER_WIDTH = 64;

const MainLayout: React.FC = () => {
  const { sidebarOpen } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isImpersonating = user?.impersonatedBy;

  const handleExitImpersonation = async () => {
    try {
      const superAdminToken = sessionStorage.getItem('superAdminToken');
      if (superAdminToken) {
        localStorage.setItem('token', superAdminToken);
        sessionStorage.removeItem('superAdminToken');
        
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${superAdminToken}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          dispatch(
            setCredentials({
              user: data.data.user,
              token: superAdminToken,
            })
          );
          navigate('/super-admin');
        } else {
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
      window.location.href = '/login';
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: 'background.default',
          minHeight: '100vh',
          width: `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH}px)`,
          transition: (theme) =>
            theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
          {isImpersonating && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              action={
                <Button color="inherit" size="small" onClick={handleExitImpersonation}>
                  Exit Impersonation
                </Button>
              }
            >
              You are viewing as {user?.email} (Impersonation Mode)
            </Alert>
          )}
          <Outlet />
        </Box>
      </Box>
      <NotificationSnackbar />
    </Box>
  );
};

export default MainLayout;